/**
 * Invite Accept Page (v0.10.0 - Issue #213)
 * 
 * Public page where users can view and accept club invites.
 * Handles both authenticated and unauthenticated users.
 */

import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOroMutation, OroOperation, type ApiErrorEnvelope } from '../hooks/useOroMutation';
import { invitesApi, type AcceptInviteResponse, type InviteDetailsResponse } from '../lib/api/invites';
import { useMe } from '../hooks/useMe';
import { useAuthModal } from '../contexts/AuthModalContext';
import { inviteLandingPath, isEventShortCode } from '../lib/bringAFriend';
import { XCircle, AlertTriangle, Ticket, Clock } from 'lucide-react';

export default function InviteAcceptPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  // #3561 "Bring a friend": `?event=<short code>` is sent to the server only
  // when it is a valid short code. After accept the page lands on the event
  // the server resolved for a share link (see onSuccess), else the club; the
  // path is built locally (inviteLandingPath), so nothing can redirect off-site.
  const [searchParams] = useSearchParams();
  const eventParam = searchParams.get('event');
  const eventShortCode = isEventShortCode(eventParam) ? eventParam : null;
  const queryClient = useQueryClient();
  const { data: me, isLoading: meLoading } = useMe();
  const { openLoginModal } = useAuthModal();
  const [acceptError, setAcceptError] = useState<string | null>(null);

  // Fetch invite details
  const {
    data: inviteData,
    isLoading: inviteLoading,
    error: inviteError,
  } = useQuery({
    queryKey: ['invite', code, eventShortCode],
    queryFn: () => invitesApi.getInviteDetails(code!, eventShortCode),
    enabled: !!code,
    retry: false,
  });

  // Accept invite mutation
  const acceptMutation = useOroMutation<AcceptInviteResponse, void>({
    mutationFn: () => invitesApi.acceptInvite(code!),
    oro: {
      op: OroOperation.AcceptInvite,
      retryPolicy: 'never',
      debugLabel: 'Invite acceptance',
    },
    onSuccess: async (data) => {
      // Refresh the clubs list (and pending invites) BEFORE navigating, so
      // TourProvider's useClubs() resolves the new club on the destination
      // page. Without this, the tour saw firstClubSlug=null on /clubs/{slug}
      // and silently filtered every club-scoped step (#1572).
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['clubs'] }),
        queryClient.invalidateQueries({ queryKey: ['pending-invites'] }),
      ]);
      // Round 2 (Frank F3): land on the event the SERVER resolved for this
      // invite (a share link, an event in its own club), never on the raw
      // param. Anything else lands on the club. Read from the cache at success
      // time so the value is current, not a stale render's.
      const resolvedEvent = queryClient.getQueryData<InviteDetailsResponse>([
        'invite',
        code,
        eventShortCode,
      ])?.invite.event?.short_code;
      navigate(inviteLandingPath(data.club_slug, resolvedEvent));
    },
    onError: (error: ApiErrorEnvelope) => {
      setAcceptError(error?.error?.message || error?.message || 'Unable to accept invite');
    },
  });

  const invite = inviteData?.invite;
  const isAuthenticated = !!me && !meLoading;

  // Check if invite is expiring soon (within 7 days)
  // NOTE: expires_at is Unix seconds, Date.now() is milliseconds
  const isExpiringSoon = useMemo(() => {
    if (!invite?.expires_at) return false;
    const sevenDaysFromNow = Date.now() + (7 * 24 * 60 * 60 * 1000);
    return (invite.expires_at * 1000) < sevenDaysFromNow;
  }, [invite?.expires_at]);

  // Handle accept button click
  const handleAccept = () => {
    if (!isAuthenticated) {
      // Store invite code and open login modal
      sessionStorage.setItem('pending_invite', code!);
      // Keep the event across sign-in (#3561): the return path carries it.
      openLoginModal(`/invite/${code}${eventShortCode ? `?event=${eventShortCode}` : ''}`);
      return;
    }

    acceptMutation.mutate();
  };

  // Auto-accept if user just logged in with pending invite
  // #895: Only clear sessionStorage on successful accept (not before mutation)
  useEffect(() => {
    const pendingInvite = sessionStorage.getItem('pending_invite');
    if (isAuthenticated && pendingInvite === code && invite?.status === 'pending' && !acceptMutation.isPending) {
      acceptMutation.mutate(undefined, {
        onSuccess: () => {
          // Clear only after successful accept
          sessionStorage.removeItem('pending_invite');
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- #3601 re-run only on auth/code/status change; acceptMutation identity is unstable
  }, [isAuthenticated, code, invite?.status]);

  if (inviteLoading || meLoading) {
    return (
      <div className="min-h-screen bg-oro-bg flex items-center justify-center">
        <div className="text-center">
          <div className="oro-loading h-12 w-12 rounded-full bg-accent mx-auto mb-4 opacity-80" />
          <p className="text-text-secondary">Loading invite...</p>
        </div>
      </div>
    );
  }

  if (inviteError || !invite) {
    return (
      <div className="min-h-screen bg-oro-bg flex items-center justify-center p-4">
        <div className="bg-oro-surface rounded-lg shadow-sm p-8 max-w-md w-full text-center">
          <XCircle className="w-12 h-12 text-oro-error mx-auto mb-4" />
          <h1 className="text-h2 font-bold text-text-primary mb-2">Invite not found</h1>
          <p className="text-text-secondary mb-6">
            This invite link is invalid or has expired.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/90 transition"
          >
            Go home
          </button>
        </div>
      </div>
    );
  }

  // Show error if invite is not pending
  if (invite.status !== 'pending') {
    const statusMessages = {
      accepted: 'This invite has already been accepted.',
      revoked: 'This invite has been revoked.',
      expired: 'This invite has expired.',
    };

    return (
      <div className="min-h-screen bg-oro-bg flex items-center justify-center p-4">
        <div className="bg-oro-surface rounded-lg shadow-sm p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <h1 className="text-h2 font-bold text-text-primary mb-2">That invite isn't available</h1>
          <p className="text-text-secondary mb-6">
            {statusMessages[invite.status as keyof typeof statusMessages] || 'This invite is no longer valid.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/90 transition"
          >
            Go home
          </button>
        </div>
      </div>
    );
  }

  const roleText = invite.type === 'club_leader' ? 'club leader' : 'member';

  return (
    <div className="min-h-screen bg-oro-bg flex items-center justify-center p-4">
      <div className="bg-oro-surface rounded-lg shadow-sm p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <Ticket className="w-12 h-12 text-accent mx-auto mb-4" />
          <h1 className="text-h2 font-bold text-text-primary mb-2">You're invited</h1>
          <p className="text-text-secondary">
            {/* #3561: a Bring-a-friend link names the event, never the sharer.
                #3828: branch on is_share_link; the server keeps a neutral
                inviter on those links for v1.11.0 clients. */}
            {invite.is_share_link
              ? invite.event
                ? `You're invited to ${invite.event.title} with`
                : "You're invited to join"
              : `${invite.inviter.name} has invited you to join`}
          </p>
        </div>

        {/* Club Info */}
        <div className="bg-oro-surface-alt border border-oro-border rounded-lg p-6 mb-6 text-center">
          {invite.club.logo_url && (
            <img
              src={invite.club.logo_url}
              alt={invite.club.name}
              className="w-20 h-20 rounded-full mx-auto mb-3 object-cover"
            />
          )}
          <h2 className="text-h3 font-bold text-text-primary mb-1">{invite.club.name}</h2>
          <p className="text-sm text-text-secondary">as a {roleText}</p>
        </div>

        {/* Role Description */}
        <div className="mb-6">
          {invite.type === 'club_leader' ? (
            <div className="bg-oro-info-soft border border-oro-info rounded-lg p-4">
              <p className="text-sm text-text-primary">
                <strong>As a club leader</strong>, you'll be able to create events, manage members,
                and help grow your club.
              </p>
            </div>
          ) : (
            <div className="bg-oro-success-soft border border-oro-success rounded-lg p-4">
              <p className="text-sm text-text-primary">
                <strong>As a member</strong>, you'll be able to RSVP for events, check in at events,
                and connect with your club.
              </p>
            </div>
          )}
        </div>

        {/* Expiration Warning */}
        {isExpiringSoon && invite.expires_at && (
          <div className="bg-oro-warning-soft border border-oro-warning rounded-lg p-4 mb-6">
            <p className="text-sm text-text-primary">
              <Clock className="w-4 h-4 inline mr-1" />This invite expires{' '}
              {new Date(invite.expires_at * 1000).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          </div>
        )}

        {/* Error Message */}
        {acceptError && (
          <div className="bg-oro-error-soft border border-oro-error-border rounded-lg p-4 mb-6">
            <p className="text-sm text-text-primary">{acceptError}</p>
          </div>
        )}

        {/* Accept Button */}
        <button
          onClick={handleAccept}
          disabled={acceptMutation.isPending}
          className="w-full bg-accent text-white py-3 rounded-lg font-semibold hover:bg-accent/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {acceptMutation.isPending
            ? 'Accepting...'
            : isAuthenticated
            ? 'Accept invite'
            : 'Sign in or sign up to accept'}
        </button>

        {/* Footer */}
        <p className="text-center text-sm text-text-muted mt-6">
          Don't want to join? You can safely ignore this invite.
        </p>
      </div>
    </div>
  );
}
